import { Primary, SelectField } from '@cinch-labs/design-system'
import { dashify, formatNumber, formatPrice } from '@cinch-labs/shared-util'
import { Datadog } from '@cinch-nx/landing-ui-shared'
import {
  EventActions,
  EventNames,
  TrackingEvent,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { datadogRum } from '@datadog/browser-rum'
import React, { useEffect, useRef, useState } from 'react'

import { getVehiclesData as fetchVehiclesData } from '../../../data/get-vehicles-data'
import {
  Bucket,
  Makes,
  Models,
  VehicleCondition,
  VehiclesData,
} from '../../../types/vehicles-data'
import { Price } from '../filters/price/price'
import styles from './search-cars-form.module.css'

export interface SearchCarsFormProps {
  vehicleCondition: VehicleCondition
  vehicleData: VehiclesData
  section?: string
}

interface FieldOption {
  value: string
  label: string
}

enum PriceFrequency {
  MonthlyPrice = 'monthly-price',
  TotalPrice = 'total-price',
}

interface PriceFrequencyOption {
  value: PriceFrequency
  label: string
}

const PRICE_FREQUENCY_OPTIONS: Readonly<PriceFrequencyOption[]> = [
  {
    value: PriceFrequency.MonthlyPrice,
    label: 'Monthly price',
  },
  {
    value: PriceFrequency.TotalPrice,
    label: 'Total price',
  },
]

const getVehiclesData = async ({
  vehicleCondition,
  priceFrequency,
  maxTotalPrice,
  make,
  model,
  vehiclesDataCache,
}: {
  vehicleCondition: VehicleCondition
  priceFrequency: PriceFrequency
  maxTotalPrice: number
  make?: string
  model?: string
  vehiclesDataCache: Map<string, VehiclesData>
}) => {
  const cacheKey = `${vehicleCondition}-${priceFrequency}-${maxTotalPrice}-${make}-${model}`
  if (vehiclesDataCache.has(cacheKey)) {
    return vehiclesDataCache.get(cacheKey) as VehiclesData
  }

  const { vehiclesData } = await fetchVehiclesData({
    vehicleCondition,
    byMonthlyPrice: priceFrequency === PriceFrequency.MonthlyPrice,
    maxTotalPrice,
    make,
    model,
  })
  vehiclesDataCache.set(cacheKey, vehiclesData)

  return vehiclesData
}

const { Actions, Context } = Datadog
const trackDropdownOptionChange = ({
  trackEventLabel,
  value,
  datadogActionName,
  trackEvent,
}: {
  trackEventLabel: string
  value: string
  datadogActionName: string
  trackEvent: (event: TrackingEvent) => void
}) => {
  const normalizedLabel = trackEventLabel.toLowerCase()
  trackEvent({
    eventName: `Homepage ${normalizedLabel} dropdown`,
    type: TrackingEventTypes.ADOBE,
    data: {
      event: {
        name: EventNames.click,
        category: 'hero',
        action: EventActions.select,
        label: normalizedLabel,
        value: value?.toLowerCase(),
      },
    },
  })

  datadogRum.addAction(datadogActionName, {
    service: Context.HERO_SEARCH,
    squad: Context.SQUAD,
    page: window.location.pathname,
    value: value?.toLowerCase(),
  })
}

export const SearchCarsForm: React.FC<SearchCarsFormProps> = ({
  vehicleCondition,
  vehicleData,
}) => {
  const { trackEvent } = useAnalytics()

  const { totalStock, makes } = vehicleData

  const filteredMakes = useRef<Makes[]>(makes)
  const [priceBucket, setPriceBucket] = useState<Bucket>(
    vehicleData.priceBucket,
  )
  const [monthlyPriceBucket, setMonthlyPriceBucket] = useState<Bucket>(
    vehicleData.monthlyPriceBucket,
  )
  const [searchResultsCount, setSearchResultsCount] =
    useState<number>(totalStock)

  const [selectedMakeOption, setSelectedMakeOption] = useState<string>()
  const [makeOptions, setMakeOptions] = useState<FieldOption[]>([
    { value: 'any', label: 'Any' },
    ...makes.map((make) => ({
      value: make.name,
      label: `${make.name} (${make.stock})`,
    })),
  ])
  const onMakeChange = (value: string) => {
    // use case for duplicate "any" values - if "any" = "any", then no change
    if (value === selectedMakeOption) return

    if (value === 'any') {
      resetModelOptions()
    } else {
      const selectedMake = filteredMakes.current.find((x) => x.name === value)
      if (!selectedMake) return

      if (selectedMake.models?.length) {
        resetModelOptions(selectedMake.models)
      }
    }

    setSelectedMakeOption(value)
    updateMakeOptions(value)

    trackDropdownOptionChange({
      trackEventLabel: 'make',
      value,
      datadogActionName: Actions.SELECT_MAKE_OPTION,
      trackEvent,
    })
  }
  const updateMakeOptions = (selectedMake?: string) => {
    // reset the make options so that only the selected make label does not display stock
    setMakeOptions([
      { value: 'any', label: 'Any' },
      ...filteredMakes.current.map((make) =>
        selectedMake && selectedMake === make.name
          ? {
              value: make.name,
              label: make.name,
            }
          : {
              value: make.name,
              label: `${make.name} (${make.stock})`,
            },
      ),
    ])
  }

  const [modelOptions, setModelOptions] = useState<FieldOption[]>([])
  const [selectedModelOption, setSelectedModelOption] = useState<string>()
  const resetModelOptions = (models?: Models[]) => {
    setSelectedModelOption(undefined)

    if (!models) {
      setModelOptions([])
    } else {
      setModelOptions([
        { value: 'any', label: 'Any' },
        ...models.map((model) => ({
          value: model.name,
          label: `${model.name} (${model.stock})`,
        })),
      ])
    }
  }
  const onModelChange = (value: string) => {
    // use case for duplicate "any" values - if "any" = "any", then no change
    if (value === selectedModelOption) return

    const selectedMake = filteredMakes.current.find(
      (x) => x.name === selectedMakeOption,
    )

    if (!selectedMake) return //should always be a selected make

    const selectedModel = selectedMake.models.find((x) => x.name === value)
    if (!selectedModel && value !== 'any') return

    updateModelOptions(selectedMake.models, value)
    setSelectedModelOption(value)

    trackDropdownOptionChange({
      trackEventLabel: 'model',
      value,
      datadogActionName: Actions.SELECT_MODEL_OPTION,
      trackEvent,
    })
  }
  const updateModelOptions = (models: Models[], selectedModel?: string) => {
    // reset the model options so that only the selected model label does not display stock
    setModelOptions([
      { value: 'any', label: 'Any' },
      ...models.map((model) =>
        selectedModel && selectedModel === model.name
          ? {
              value: model.name,
              label: model.name,
            }
          : {
              value: model.name,
              label: `${model.name} (${model.stock})`,
            },
      ),
    ])
  }

  const [selectedPriceFrequencyOption, setSelectedPriceFrequencyOption] =
    useState<PriceFrequency>(PRICE_FREQUENCY_OPTIONS[0].value)
  const onPriceFrequencyChange = (value: string, label: string) => {
    if (
      value !== PRICE_FREQUENCY_OPTIONS[0].value &&
      value !== PRICE_FREQUENCY_OPTIONS[1].value
    ) {
      return
    }

    setSelectedMaxTotalPrice(0)
    setSelectedPriceFrequencyOption(value)

    const datadogActionName =
      value === PriceFrequency.MonthlyPrice
        ? Actions.CLICK_MONTHLY_PRICE_RADIO
        : Actions.CLICK_TOTAL_PRICE_RADIO
    trackDropdownOptionChange({
      trackEventLabel: label,
      value: `${value} checked`,
      datadogActionName: datadogActionName,
      trackEvent,
    })
  }

  const [selectedMaxTotalPrice, setSelectedMaxTotalPrice] = useState<number>(0)
  useEffect(() => {
    refreshSearchResults()
  }, [
    selectedMakeOption,
    selectedModelOption,
    selectedPriceFrequencyOption,
    selectedMaxTotalPrice,
  ])

  const vehiclesDataCache = useRef<Map<string, VehiclesData>>(
    new Map<string, VehiclesData>([
      [
        `${vehicleCondition}-${selectedPriceFrequencyOption}-${selectedMaxTotalPrice}-${selectedMakeOption}-${selectedModelOption}`,
        vehicleData,
      ],
    ]),
  )
  const refreshSearchResults = ({
    selectedMake = selectedMakeOption,
    selectedModel = selectedModelOption,
    selectedPriceFrequency = selectedPriceFrequencyOption,
  }: {
    selectedPriceFrequency?: PriceFrequency
    selectedMake?: string
    selectedModel?: string
  } = {}) => {
    const make =
      selectedMake && selectedMake !== 'any' ? selectedMake : undefined
    const model =
      selectedMake && selectedModel && selectedModel !== 'any'
        ? selectedModel
        : undefined

    getVehiclesData({
      vehicleCondition,
      priceFrequency: selectedPriceFrequency,
      maxTotalPrice: selectedMaxTotalPrice,
      make,
      model,
      vehiclesDataCache: vehiclesDataCache.current,
    })
      .then((vehiclesData) => {
        filteredMakes.current = vehiclesData.makes
        setPriceBucket(vehiclesData.priceBucket)
        setMonthlyPriceBucket(vehiclesData.monthlyPriceBucket)
        setSearchResultsCount(vehiclesData.totalStock)

        updateMakeOptions(selectedMake)

        const currentMake = filteredMakes.current.find(
          (x) => x.name === selectedMake,
        )
        if (currentMake) {
          updateModelOptions(currentMake.models, selectedModel)
        }
      })
      .catch((error) => {
        console.log('Unable to update search results count:', error)
        datadogRum.addError(error)
      })
  }

  const trackOnSubmit = () => {
    const make = selectedMakeOption ?? 'any'
    const model = selectedModelOption ?? 'any'

    // defaults for any price could be -1 or 0
    const formattedPrice =
      selectedMaxTotalPrice < 1 ? 'any' : formatPrice(selectedMaxTotalPrice)

    const priceSnippet =
      selectedPriceFrequencyOption === PriceFrequency.MonthlyPrice
        ? `monthly price: ${formattedPrice}`
        : `total price: ${formattedPrice}`

    const trackingValue = `search cars make: ${make}, model: ${model}, ${priceSnippet}`

    trackEvent({
      eventName: `Click on 'Search x ${vehicleCondition} cars' CTA button`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'hero',
          action: 'submit',
          label: 'search cars',
          value: trackingValue,
          type: vehicleCondition,
        },
      },
    })

    datadogRum.addAction(Actions.CLICK_SEARCH_CTA, {
      service: Context.HERO_SEARCH,
      squad: Context.SQUAD,
      page: window.location.pathname,
      value: trackingValue,
    })
  }

  const onSubmit = () => {
    const urlSegments = [
      `/${vehicleCondition}-cars`,
      ...(selectedMakeOption && selectedMakeOption !== 'any'
        ? [`${dashify(selectedMakeOption)}`]
        : []),
      ...(selectedModelOption && selectedModelOption !== 'any'
        ? [`${dashify(selectedModelOption)}`]
        : []),
    ]

    const urlParams = new URLSearchParams()
    if (selectedPriceFrequencyOption === PriceFrequency.MonthlyPrice) {
      urlParams.append('financeType', 'any')
    }

    const baseUrl = urlSegments.join('/')
    const redirectUrl =
      urlParams.toString().length > 0
        ? `${baseUrl}?${urlParams.toString()}`
        : baseUrl

    trackOnSubmit()
    window.location.assign(redirectUrl)
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      <fieldset className={styles['radioGroup']}>
        {PRICE_FREQUENCY_OPTIONS.map((option) => (
          <div key={option.value} className={styles['radioInputContainer']}>
            <label htmlFor={option.value} className={styles['priceLabel']}>
              <input
                type="radio"
                id={option.value}
                name={option.value}
                checked={option.value === selectedPriceFrequencyOption}
                onChange={() =>
                  onPriceFrequencyChange(option.value, option.label)
                }
              />
              {option.label}
            </label>
          </div>
        ))}
      </fieldset>
      <Price
        radioChecked={selectedPriceFrequencyOption}
        maxTotalPrice={selectedMaxTotalPrice}
        setMaxTotalPrice={setSelectedMaxTotalPrice}
        priceBucket={priceBucket}
        monthlyPriceBucket={monthlyPriceBucket}
        trackPriceChange={(value) => {
          const isMonthly = selectedPriceFrequencyOption
            .toLowerCase()
            .includes('month')
          trackDropdownOptionChange({
            trackEventLabel: selectedPriceFrequencyOption.replace('-', ' '),
            value,
            datadogActionName: isMonthly
              ? Actions.SELECT_MONTHLY_PRICE_OPTION
              : Actions.SELECT_MAX_PRICE_OPTION,
            trackEvent,
          })
        }}
      />
      <SelectField
        label="Select make"
        labelHidden
        id="make"
        name="make"
        data-testid="make-select"
        options={makeOptions}
        placeholder={'Select make'}
        placeholderValue={'any'}
        onChange={(event) => onMakeChange(event.target.value)}
        error={{
          show: false,
          classNames: { container: styles['errorBox'] },
        }}
      />
      <SelectField
        label="Select model"
        labelHidden
        id="model"
        name="model"
        data-testid="model-select"
        options={modelOptions}
        placeholder={'Select model'}
        placeholderValue={'any'}
        onChange={(event) => onModelChange(event.target.value)}
        disabled={!selectedMakeOption || selectedMakeOption === 'any'}
        error={{
          show: false,
          classNames: { container: styles['errorBox'] },
        }}
      />

      <div className={styles['buttonContainer']}>
        <Primary
          type="submit"
          classNames={{ button: styles['button'] }}
          data-testid="searchButton"
          isFullWidth
        >
          {searchResultsCount === 1
            ? 'Search 1 car'
            : `Search ${formatNumber(searchResultsCount)} cars`}
        </Primary>
      </div>
    </form>
  )
}
